import React, { useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Line } from "@react-three/drei";
import { polygon, walls } from "./cadGeometry";
import { useSurfaceTexture, roughnessFor } from "./tileTexture";
import { Fixture3D } from "./Fixture3D";

const Wall = ({ w, H, spec, col, paint, onPick }) => {
  const tex = useSurfaceTexture({ wM: w.len, hM: H, spec: spec || {}, col, paint });
  return (
    <mesh position={[(w.a[0] + w.b[0]) / 2, H / 2, (w.a[1] + w.b[1]) / 2]} rotation={[0, Math.atan2(w.nx, w.nz), 0]} receiveShadow onClick={(e) => { if (e.delta < 4) { e.stopPropagation(); onPick?.(w.key); } }}>
      <planeGeometry args={[w.len, H]} />
      <meshStandardMaterial key={tex?.uuid || "none"} map={tex} color={tex ? "#ffffff" : paint} roughness={roughnessFor(col)} />
    </mesh>
  );
};

const Floor = ({ room, spec, col, onPick }) => {
  const geo = useMemo(() => {
    const s = new THREE.Shape();
    polygon(room).forEach(([x, z], i) => (i ? s.lineTo(x, -z) : s.moveTo(x, -z)));
    return new THREE.ShapeGeometry(s);
  }, [room]);
  const tex = useSurfaceTexture({ wM: room.length, hM: room.width, spec: { ...(spec || {}), tiled_height: null }, col, paint: "#cfc8bc" });
  if (tex) { tex.repeat.set(1 / room.length, 1 / room.width); tex.offset.set(0, 1); }
  return (
    <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} receiveShadow onClick={(e) => { if (e.delta < 4) onPick?.("floor"); }}>
      <meshStandardMaterial key={tex?.uuid || "none"} map={tex} color={tex ? "#ffffff" : "#cfc8bc"} roughness={roughnessFor(col)} side={THREE.DoubleSide} />
    </mesh>
  );
};

const Scene = ({ state, collections, meta, brassware = [], selected, onSelect, onMove, onDragStart, onSurface, readOnly }) => {
  const { room } = state;
  const metal = brassware.find((b) => b.id === state.brassware)?.colour || "#1b1b1b";
  const byId = useMemo(() => Object.fromEntries(collections.map((c) => [c.id, c])), [collections]);
  const ws = useMemo(() => walls(room), [room]);
  const [dragging, setDragging] = useState(null);
  const top = useMemo(() => { const p = polygon(room).map(([x, z]) => [x, room.height, z]); return [...p, p[0]]; }, [room]);
  const snap = (v) => Math.round(v * 20) / 20;
  return (
    <>
      <color attach="background" args={["#e9e2d6"]} />
      <ambientLight intensity={0.55} />
      <hemisphereLight args={["#fff8ec", "#3a342c", 0.5]} />
      <directionalLight position={[room.length * 0.8, room.height * 3, room.width * 2]} intensity={1.4} castShadow shadow-mapSize={[2048, 2048]} shadow-camera-left={-6} shadow-camera-right={6} shadow-camera-top={6} shadow-camera-bottom={-6} />
      <pointLight position={[room.length / 2, room.height - 0.2, room.width / 2]} intensity={6} distance={8} color="#ffe2b8" />
      <Floor room={room} spec={state.surfaces.floor} col={byId[state.surfaces.floor?.tile_id]} onPick={onSurface} />
      {ws.map((w) => <Wall key={w.key} w={w} H={room.height} spec={state.surfaces[w.key]} col={byId[state.surfaces[w.key]?.tile_id]} paint={state.paint} onPick={onSurface} />)}
      <Line points={top} color="#6b1e1a" lineWidth={1.2} />
      {state.fixtures.map((f) => (
        <Fixture3D key={f.uid} f={f} meta={meta[f.type]} metal={metal} selected={selected === f.uid}
          onPointerDown={readOnly ? undefined : (e) => { e.stopPropagation(); onSelect(f.uid); onDragStart?.(); setDragging(f.uid); }} />
      ))}
      {!readOnly && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[room.length / 2, 0.002, room.width / 2]}
          onPointerMove={(e) => { if (dragging) onMove(dragging, snap(e.point.x), snap(e.point.z)); }}
          onPointerUp={() => setDragging(null)} onPointerLeave={() => setDragging(null)}>
          <planeGeometry args={[40, 40]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
      <ContactShadows position={[room.length / 2, 0.004, room.width / 2]} scale={Math.max(room.length, room.width) * 1.4} opacity={0.45} blur={2.4} far={1.5} />
      <OrbitControls makeDefault enabled={!dragging} target={[room.length / 2, room.height * 0.35, room.width / 2]} maxPolarAngle={Math.PI / 2.05} minDistance={1.2} maxDistance={16} />
    </>
  );
};

export const Room3D = (props) => {
  const { state, glRef } = props;
  const { room } = state;
  const cam = useRef([room.length * 1.2, room.height * 1.45, room.width * 1.85]);
  return (
    <Canvas shadows dpr={[1, 2]} camera={{ position: cam.current, fov: 45 }} gl={{ preserveDrawingBuffer: true, antialias: true }}
      onCreated={({ gl }) => { if (glRef) glRef.current = gl; }} onPointerMissed={() => props.onSelect?.(null)} data-testid="cad-3d-canvas">
      <Scene {...props} />
    </Canvas>
  );
};

export default Room3D;
